/**
 * The real Overview dashboard, rendered with example data and tilted in 3D,
 * for the right-hand column of the auth pages. It reuses the dashboard's own
 * components so it stays in step with the product.
 */
import { useEffect, useRef } from "react";
import type { Alert, DashboardStats, WatchedUrl } from "@/types";
import { DuotoneIcon, Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/layout/PageHeader";
import { navItems, WorkspaceLogo } from "@/components/layout/Sidebar";
import { DashboardStatsCard } from "@/components/dashboard/DashboardStats";
import { ActivityChart, MonitoringPipeline, PipelineItem } from "@/pages/DashboardPage";
import { cn } from "@/lib/utils";

// ─── Example data ─────────────────────────────────────────────────────────────

const HOUR = 60 * 60 * 1000;
const ago = (hours: number) => new Date(Date.now() - hours * HOUR).toISOString();

function lastSevenDays(counts: number[]) {
  return counts.map((count, i) => {
    const day = new Date(Date.now() - (counts.length - 1 - i) * 24 * HOUR);
    return { date: day.toISOString().slice(0, 10), count };
  });
}

const competitor = (id: string, name: string, domain: string) => ({
  id,
  workspaceId: "example",
  name,
  domain,
  faviconUrl: null,
  createdAt: ago(24 * 30),
  urlCount: 3,
});

const COMPETITORS = {
  northwind: competitor("c1", "Northwind AI", "northwind.ai"),
  lumen: competitor("c2", "Lumen Labs", "lumenlabs.io"),
  parallel: competitor("c3", "Parallel", "parallel.dev"),
  cobalt: competitor("c4", "Cobalt", "cobalt.so"),
};

const watched = (
  id: string,
  c: keyof typeof COMPETITORS,
  path: string,
  label: WatchedUrl["label"],
  lastStatus: WatchedUrl["lastStatus"],
  hoursAgo: number | null,
): WatchedUrl => ({
  id,
  workspaceId: "example",
  competitorId: COMPETITORS[c].id,
  competitor: COMPETITORS[c],
  url: `https://${COMPETITORS[c].domain}${path}`,
  label,
  crawlFrequency: "hourly",
  lastCrawledAt: hoursAgo === null ? null : ago(hoursAgo),
  lastStatus,
  isActive: true,
  createdAt: ago(24 * 30),
});

const WATCHED: WatchedUrl[] = [
  watched("w1", "northwind", "/pricing", "pricing", "success", 0.2),
  watched("w2", "lumen", "/careers", "careers", "success", 0.4),
  watched("w3", "parallel", "/", "homepage", "running", 0.05),
  watched("w4", "cobalt", "/changelog", "changelog", "success", 0.7),
  watched("w5", "northwind", "/docs", "docs", "success", 1),
  watched("w6", "parallel", "/pricing", "pricing", "success", 1.2),
  watched("w7", "lumen", "/features", "features", "blocked", 2),
  watched("w8", "cobalt", "/pricing", "pricing", "pending", null),
];

const alert = (
  id: string,
  w: WatchedUrl,
  signalScore: Alert["signalScore"],
  summary: string,
  hoursAgo: number,
  status: Alert["status"] = "read",
): Alert => ({
  id,
  workspaceId: "example",
  watchedUrlId: w.id,
  watchedUrl: w,
  competitor: w.competitor,
  signalScore,
  pageType: w.label,
  summary,
  changeExcerpt: null,
  detectedAt: ago(hoursAgo),
  status,
  deliveredVia: ["slack"],
  feedback: null,
  reasoning: null,
  hasEvidence: true,
});

const ALERTS: Alert[] = [
  alert("a1", WATCHED[0], "high", "Pro plan dropped from $49 to $39 a month and the annual discount is gone. Likely pressure on the mid tier.", 0.3, "unread"),
  alert("a2", WATCHED[2], "high", "Headline changed from \"The AI platform for developers\" to \"for enterprise teams\". A move upmarket.", 2, "unread"),
  alert("a3", WATCHED[1], "medium", "Three new Enterprise Account Executive roles in London. Points to an EMEA sales push.", 5),
  alert("a4", WATCHED[3], "low", "SSO and audit logs added to the Team plan in this week's release notes.", 9),
  alert("a5", WATCHED[4], "medium", "New batch API endpoint documented, marked beta. No announcement yet.", 20),
];

const STATS: DashboardStats = {
  totalAlerts: 33,
  unreadAlerts: 2,
  watchedUrls: WATCHED.length,
  competitors: 8,
  alertsLast7Days: lastSevenDays([3, 5, 2, 6, 4, 8, 5]),
  signalBreakdown: [
    { score: "high", count: 6 },
    { score: "medium", count: 14 },
    { score: "low", count: 13 },
  ],
  previousWeekAlerts: 26,
  competitorLimit: 10,
};

// ─── Static app shell ─────────────────────────────────────────────────────────

/** The desktop sidebar's markup, with Overview selected and nothing clickable. */
function PreviewSidebar() {
  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-border bg-card">
      <div className="flex h-14 items-center gap-2 border-b border-border px-3">
        <div className="flex flex-1 items-center gap-2.5 px-1 py-1 min-w-0">
          <WorkspaceLogo src={null} name="Acme AI" />
          <p className="flex-1 truncate font-mono text-[11px] font-semibold uppercase tracking-widest">Acme AI</p>
          <Icon name="arrow-down-01" size={14} className="text-muted-foreground shrink-0" />
        </div>
        <span className="h-7 w-7 shrink-0 flex items-center justify-center text-muted-foreground">
          <Icon name="panel-left-close" size={16} />
        </span>
      </div>

      <nav className="flex-1 space-y-0.5 px-2 py-3">
        {navItems.map(({ to, icon, label }) => (
          <div
            key={to}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2",
              "font-mono text-[11px] uppercase tracking-widest",
              to === "/dashboard" ? "bg-muted text-foreground" : "text-muted-foreground",
            )}
          >
            <DuotoneIcon name={icon} size={18} className="shrink-0" />
            {label}
          </div>
        ))}
      </nav>

      <Separator />
      <div className="p-3">
        <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
          <div className="h-6 w-6 shrink-0 rounded-full border border-border bg-muted flex items-center justify-center">
            <span className="font-mono text-[11px] uppercase">J</span>
          </div>
          <p className="flex-1 truncate font-sans text-xs font-medium">Jordan Lee</p>
          <DuotoneIcon name="logout" size={12} className="text-muted-foreground" />
        </div>
      </div>
    </aside>
  );
}

function PreviewDashboard() {
  return (
    <div className="flex h-full w-full overflow-hidden bg-background text-foreground">
      <PreviewSidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <PageHeader
          title="Overview"
          actions={
            <Button variant="ghost" size="icon" className="h-8 w-8" tabIndex={-1}>
              <Icon name="search-01" size={15} />
            </Button>
          }
        />
        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-1 flex-col gap-6 overflow-hidden p-6">
            <DashboardStatsCard stats={STATS} isLoading={false} />
            <div>
              <div className="flex items-center justify-between pb-3 mb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="file-01" size={12} className="text-muted-foreground" />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    Intelligence Feed
                  </span>
                </div>
              </div>
              {ALERTS.map((a, i) => (
                <PipelineItem key={a.id} alert={a} isLast={i === ALERTS.length - 1} />
              ))}
            </div>
            <ActivityChart data={STATS.alertsLast7Days} isLoading={false} />
          </div>
          <div className="flex w-72 shrink-0 flex-col overflow-hidden border-l border-border p-5">
            <div className="flex items-center justify-between pb-3 mb-5 border-b border-border">
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Monitoring Pipeline</span>
              <span className="font-mono text-[11px] text-muted-foreground">+ Add</span>
            </div>
            <MonitoringPipeline watchedUrls={WATCHED} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── The tilted frame ─────────────────────────────────────────────────────────

// Resting tilt, in degrees
const BASE = { x: 22, y: -16, z: 7 };
// How far the pointer can lean it, and how far the idle drift wanders
const POINTER_RANGE = { x: 6, y: 8 };
const DRIFT = { x: 1.5, y: 2 };
const DRIFT_PERIOD_MS = 9000;
const ENTRANCE_MS = 1400;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

function tiltTransform(x: number, y: number, z: number, scale: number, lift: number) {
  return `translateY(${lift}px) rotateX(${x}deg) rotateY(${y}deg) rotateZ(${z}deg) scale(${scale})`;
}

/**
 * Moves the frame every animation frame: eases in from flat on load, drifts
 * slowly while idle, and leans toward the pointer. Writes the transform
 * straight to the element so React doesn't re-render 60 times a second.
 */
function useLiveTilt(frame: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.style.transform = tiltTransform(BASE.x, BASE.y, BASE.z, 0.78, 0);
      return;
    }

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    const onPointer = (e: PointerEvent) => {
      // -1..1 across the window
      target.x = (e.clientY / window.innerHeight) * 2 - 1;
      target.y = (e.clientX / window.innerWidth) * 2 - 1;
    };
    const onLeave = () => {
      target.x = 0;
      target.y = 0;
    };
    window.addEventListener("pointermove", onPointer);
    document.addEventListener("pointerleave", onLeave);

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      // Smoothly follow the pointer
      current.x += (target.x - current.x) * 0.06;
      current.y += (target.y - current.y) * 0.06;

      const phase = ((now - start) / DRIFT_PERIOD_MS) * Math.PI * 2;
      const entrance = easeOutCubic(Math.min(1, (now - start) / ENTRANCE_MS));

      const x = BASE.x * entrance - current.x * POINTER_RANGE.x + Math.sin(phase) * DRIFT.x;
      const y = BASE.y * entrance + current.y * POINTER_RANGE.y + Math.cos(phase * 0.8) * DRIFT.y;
      const z = BASE.z * entrance;
      const scale = 0.74 + 0.04 * entrance;
      const lift = (1 - entrance) * 40 + Math.sin(phase) * 4;

      el.style.transform = tiltTransform(x, y, z, scale, lift);
      el.style.opacity = String(Math.min(1, entrance * 1.5));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointer);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, [frame]);
}

export function DashboardPreview({ className }: { className?: string }) {
  const frame = useRef<HTMLDivElement>(null);
  useLiveTilt(frame);

  // Decorative: keep links and buttons inside it out of the tab order and screen readers
  useEffect(() => frame.current?.setAttribute("inert", ""), []);

  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none select-none [perspective:2400px]", className)}
    >
      <div
        ref={frame}
        className="h-[820px] w-[1280px] origin-top-left overflow-hidden rounded-xl border border-border bg-background shadow-[0_40px_120px_-20px_hsl(var(--foreground)/0.25)] will-change-transform"
        style={{ transform: tiltTransform(BASE.x, BASE.y, BASE.z, 0.78, 0) }}
      >
        <PreviewDashboard />
      </div>
    </div>
  );
}
